import { observer } from "mobx-react-lite";
import React, { type ComponentType, type LazyExoticComponent, Suspense, useMemo } from "react";
import { Outlet, type RouteObject, useRoutes } from "react-router-dom";
import { PageSkeleton, Sidebar } from "@components/index";
import { useAppStore } from "@stores/useStores";
import { LayoutContainer } from "@layout/index";

interface RouteProps {
    path: string;
    element: LazyExoticComponent<ComponentType>;
    children?: RouteObject[];
}

const defaultRoutes: RouteProps[] = [
    {
        path: "/",
        element: React.lazy(() => import("@pages/Home"))
    },
    {
        path: "/satellites",
        element: React.lazy(() => import("@pages/Satellites"))
    },
    {
        path: "/satellites/:id",
        element: React.lazy(() => import("@pages/Satellite"))
    },
    {
        path: "/satellites/:id/beams/:beamId",
        element: React.lazy(() => import("@pages/BeamDetail"))
    },
    {
        path: "/airlines",
        element: React.lazy(() => import("@pages/Airlines"))
    },
    {
        path: "/legacy",
        element: React.lazy(() => import("@pages/Legacy"))
    },
    {
        path: "/legacy/beams",
        element: React.lazy(() => import("@pages/LegacyBeams"))
    },
    {
        path: "*",
        element: React.lazy(() => import("@pages/NotFound"))
    }
];

const generateRoutes = (routes: RouteProps[]): RouteObject[] => {
    return routes.map(route => ({
        path: route.path,
        element: (
            <Suspense fallback={<PageSkeleton />}>
                <route.element />
            </Suspense>
        )
    }));
};

const Router = observer(function Router(): React.ReactElement | null {
    const appStore = useAppStore();

    const routes: RouteObject[] = useMemo(() => [
        {
            path: "/",
            element: <LayoutContainer><Outlet /></LayoutContainer>,
            children: generateRoutes(defaultRoutes)
        }
    ], []);

    const routeElement = useRoutes(routes);

    if (!appStore.loaded) {
        return (
            <div className="min-h-screen bg-slate-950">
                <PageSkeleton />
            </div>
        );
    }

    return (
        <div className="app-container flex h-screen overflow-hidden bg-slate-950">
            <Sidebar />
            <div id="main-content" className="flex-1 overflow-y-auto">
                {routeElement}
            </div>
        </div>
    );
});

export default Router;
